import type { FC, KeyboardEventHandler } from 'react';
import { useState } from 'react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
    Flex,
    IconButton,
    InputRightElement,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    useBreakpointValue,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    type GiftCardBalanceWithRecaptcha,
    useGiftCardCheckBalance,
} from '../../../../bff-api/gift-card/useGiftCardCheckBalance';
import { BFFHttpError } from '../../../../bff-api/model/BFFHttpError';
import { BasicError } from '../../../../components/basic-error/BasicError';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { Field } from '../../../../components/field';
import { useGiftCardFields } from '../../../../components/forms/useGiftCardFields';
import { CheckCircleGreenIcon } from '../../../../components/icons';
import {
    RecaptchaWorkflowState,
    useRecaptchaWorkflow,
} from '../../../../recaptcha/hooks/useRecaptchaWorkflow';
import { RecaptchaFeature } from '../../../../recaptcha/recaptcha';
import { Button, Modal, Text } from '../../../../ui-components';
import type { OrderPaymentInstrument } from '../../../../bff-api/baskets/model';
import type { RecaptchaAction } from '../../../../recaptcha/recaptcha';

const ERROR_MESSAGES = {
    giftCardNumber: {
        id: 'enter.valid.gift.card.number',
        defaultMessage: 'Enter a valid gift card number to continue',
    },
    giftCardPin: {
        id: 'enter.valid.gift.card.pin',
        defaultMessage: 'Please enter a valid gift PIN number to continue',
    },
};

export interface CheckBalanceModalProps {
    id: string;
    isOpen: boolean;
    onClose: () => void;
    action: RecaptchaAction;
}

export const CheckBalanceModal: FC<CheckBalanceModalProps> = ({ id, isOpen, onClose, action }) => {
    const [balance, setBalance] = useState<number | null>(null);

    const intl = useIntl();
    const modalOnly = useBreakpointValue({
        base: false,
        sm: true,
    });

    const checkBalance = useGiftCardCheckBalance();

    const recaptchaWorkflow = useRecaptchaWorkflow({
        feature: RecaptchaFeature.GIFT_CARD_BALANCE,
        action,
        autoSubmitOnV2: true,
        onV2Complete: async () => {
            await form.handleSubmit(onSubmit)();
        },
    });

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = async (e) => {
        if (e.key === 'Enter') {
            await form.handleSubmit(onSubmit)();
        }
    };

    const form = useForm<OrderPaymentInstrument>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
    });
    const fields = useGiftCardFields(form, onKeyDown);

    const onSubmit = async (formData: OrderPaymentInstrument): Promise<void> => {
        try {
            const recaptchaPayload = await recaptchaWorkflow.getRecaptchaPayload();

            const data: GiftCardBalanceWithRecaptcha = {
                cardNumber: formData.c_GiftCardNumber!,
                pin: formData.c_GiftCardPin!,
                recaptcha: recaptchaPayload,
            };

            const result = await checkBalance.mutateAsync(data);

            setBalance(result.balance!);
            recaptchaWorkflow.handleApiSuccess();
        } catch (error) {
            setBalance(null);
            form.clearErrors();

            const wasHandled = recaptchaWorkflow.handleApiError(error);
            if (wasHandled) {
                return;
            }

            if (error instanceof BFFHttpError && error.body?.detail) {
                if (
                    /gift card number is invalid/i.test(error.body?.detail) ||
                    /Unknown error occurred/i.test(error.body?.detail)
                ) {
                    form.setError('c_GiftCardNumber', {
                        type: 'validate',
                        message: intl.formatMessage(ERROR_MESSAGES.giftCardNumber),
                    });
                } else if (/Invalid PIN Number or PIN Locked/i.test(error.body?.detail)) {
                    form.setError('c_GiftCardPin', {
                        type: 'validate',
                        message: intl.formatMessage(ERROR_MESSAGES.giftCardPin),
                    });
                } else if (error.body?.type === 'svs') {
                    form.setError('global', {
                        type: 'validate',
                        message: error.body?.detail,
                    });
                }
            }
        }
    };

    const onClickCheckAnotherCard = (): void => {
        form.reset();
        setBalance(null);
        recaptchaWorkflow.resetWorkflow();
    };

    const onCloseModal = (): void => {
        form.reset({
            c_GiftCardNumber: '',
            c_GiftCardPin: '',
        });

        setBalance(null);
        recaptchaWorkflow.resetWorkflow();

        onClose();
    };

    return (
        <Modal
            id={id}
            title={intl.formatMessage({
                id: 'check.balance',
                defaultMessage: 'Check Balance',
            })}
            isOpen={isOpen}
            onClose={onCloseModal}
            modalOnly={modalOnly}
        >
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                <Flex direction="column" gap={6}>
                    <Flex direction="column" gap={4}>
                        <Field {...fields.c_GiftCardNumber} />

                        <Popover
                            variant="slt-tooltip"
                            placement="top-end"
                            trigger="hover"
                            flip={false}
                            gutter={-5}
                        >
                            <Field {...fields.c_GiftCardPin}>
                                <PopoverTrigger>
                                    <InputRightElement>
                                        <IconButton
                                            data-testid={`${id}-gift-card-pin-icon`}
                                            variant="ghost"
                                            _hover={{ cursor: 'default' }}
                                            aria-label="secure lock icon"
                                            icon={<InfoOutlineIcon />}
                                        />
                                    </InputRightElement>
                                </PopoverTrigger>

                                <PopoverContent padding={0} maxW="max-content">
                                    <PopoverArrow />
                                    <PopoverBody
                                        aria-label="Find the card number and PIN in the recipient's email for e-gift cards, or on the back of your physical card."
                                        textStyle="slt-body-text"
                                        padding={2}
                                    >
                                        <Text as="span">
                                            <FormattedMessage
                                                id="find.card.number.and.pin"
                                                defaultMessage="Find the card number and PIN in the recipient's email for e-gift cards, or on the back of your physical card."
                                            />
                                        </Text>
                                    </PopoverBody>
                                </PopoverContent>
                            </Field>
                        </Popover>
                    </Flex>

                    {balance !== null ? (
                        <Flex gap={2}>
                            <CheckCircleGreenIcon
                                boxSize={6}
                                data-testid={`${id}-check-circle-icon`}
                            />

                            <Text id={`${id}-balance-message`} style="bold">
                                <FormattedMessage
                                    id="the.balance.is"
                                    defaultMessage="The balance is: {value}"
                                    values={{
                                        value: `$${balance}`,
                                    }}
                                />
                            </Text>
                        </Flex>
                    ) : null}

                    <ConditionalRender visibility={!!recaptchaWorkflow.errorMessage}>
                        <div className="col-span-2 mt-1 flex items-center justify-start">
                            <Text color="slt-red-error">{recaptchaWorkflow.errorMessage}</Text>
                        </div>
                    </ConditionalRender>
                    <ConditionalRender
                        visibility={recaptchaWorkflow.state === RecaptchaWorkflowState.V2_REQUIRED}
                    >
                        {recaptchaWorkflow.V2Widget}
                    </ConditionalRender>

                    {balance === null ? (
                        <Button
                            id={`${id}-check-balance-button`}
                            variant={balance === null ? 'primary-black' : 'secondary'}
                            wide
                            isLoading={checkBalance.isLoading}
                            isDisabled={recaptchaWorkflow.isBlocked}
                            type="submit"
                        >
                            <FormattedMessage id="check.balance" defaultMessage="Check Balance" />
                        </Button>
                    ) : (
                        <Button
                            id={`${id}-check-another-card-button`}
                            variant="secondary"
                            wide
                            onClick={onClickCheckAnotherCard}
                        >
                            <FormattedMessage
                                id="check.another.card"
                                defaultMessage="Check Another Card"
                            />
                        </Button>
                    )}

                    {form.formState.errors.global?.message ? (
                        <BasicError
                            options={{
                                alertDataTestId: `${id}-error-container`,
                                errorMessageDataTestId: `${id}-error-message`,
                            }}
                            margin="0"
                        >
                            {form.formState.errors.global.message as string}
                        </BasicError>
                    ) : null}
                </Flex>
            </form>
        </Modal>
    );
};
